import {
  Paper,
  Typography,
  Box,
  Chip,
  Button,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import { AccessTime, GridView } from "@mui/icons-material";

/**
 * =====================================================
 * EXAM HEADER
 * -----------------------------------------------------
 * Supports:
 * - Subject exams
 * - JAMB (multi-subject)
 *
 * UI ONLY:
 * - Subject name
 * - Subject index (1 of 4)
 * - Progress bar (animated)
 * - Green color on final subject
 * =====================================================
 */

export default function ExamHeader({
  exam,
  minutes,
  seconds,
  setMapOpen,

  /* JAMB ONLY (OPTIONAL) */
  subjectIndex = null,
  totalSubjects = null,
  subjectProgress = 0,

  /* EXISTING */
  saving = false,
  navBusy = false,
  submittingUI = false,
}) {
  const busy = saving || navBusy || submittingUI;

  /* ================= TIMER COLOR LOGIC ================= */
  const totalSeconds = minutes * 60 + seconds;

  let timerColor = "#ffeb3b"; // yellow
  let textColor = "#0d47a1";

  if (totalSeconds <= 300) {
    timerColor = "#fecaca"; // light red
    textColor = "#7f1d1d";
  }

  if (totalSeconds <= 120) {
    timerColor = "#dc2626"; // strong red
    textColor = "#ffffff";
  }

  const isJamb =
    exam?.exam_type === "JAMB" &&
    Number.isInteger(subjectIndex) &&
    Number.isInteger(totalSubjects);

  const isFinalSubject = isJamb && subjectIndex === totalSubjects;

  const subjectName =
    exam?.subject_name ||
    exam?.subject?.name ||
    "Subject";

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 1.5, sm: 2 },
        mb: 2,
        borderRadius: 3,
        backgroundColor: "#0d47a1",
        color: "white",
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", sm: "center" },
        gap: { xs: 1, sm: 0 },
      }}
    >
      {/* ================= LEFT: SUBJECT INFO ================= */}
      <Box sx={{ width: "100%" }}>
        {/* SUBJECT NAME */}
        <Typography
          variant="h6"
          fontWeight={800}
          sx={{
            fontSize: { xs: "1rem", sm: "1.25rem" },
            lineHeight: 1.25,
            wordBreak: "break-word",
          }}
        >
          {subjectName}
        </Typography>

        {/* SUBJECT POSITION + PROGRESS */}
        {isJamb && (
          <Box sx={{ mt: 0.8 }}>
            {/* LABEL */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={0.5}
            >
              <Typography
                fontSize={13}
                fontWeight={700}
              >
                {subjectName} ({subjectIndex} of {totalSubjects})
              </Typography>

              <Typography
                fontSize={12}
                fontWeight={800}
                color={isFinalSubject ? "#22c55e" : "#bbdefb"}
              >
                {subjectProgress}%
              </Typography>
            </Box>

            {/* PROGRESS BAR */}
            <LinearProgress
              variant="determinate"
              value={subjectProgress}
              sx={{
                height: 8,
                borderRadius: 6,
                backgroundColor: "rgba(255,255,255,0.25)",

                "& .MuiLinearProgress-bar": {
                  borderRadius: 6,
                  backgroundColor: isFinalSubject
                    ? "#22c55e"
                    : "#90caf9",

                  /* 🔥 SMOOTH ANIMATION */
                  transition: "width 0.6s ease-in-out",
                },
              }}
            />
          </Box>
        )}
      </Box>

      {/* ================= RIGHT: TIMER + MAP ================= */}
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        sx={{
          width: { xs: "100%", sm: "auto" },
          justifyContent: { xs: "space-between", sm: "flex-end" },
        }}
      >
        {/* TIMER */}
        <Chip
          icon={<AccessTime sx={{ color: "#0d47a1" }} />}
          label={`${minutes}:${seconds.toString().padStart(2, "0")}`}
          sx={{
            backgroundColor: timerColor,
            color: textColor,
            fontWeight: "bold",
            fontSize: { xs: "0.85rem", sm: "0.95rem" },
            height: 32,
            minWidth: 92,
            display: "flex",
            justifyContent: "center",
            transition: "background-color 0.6s ease",
          }}
        />

        {/* MOBILE MAP BUTTON */}
        <Button
          variant="outlined"
          disabled={busy}
          onClick={() => setMapOpen(true)}
          startIcon={
            busy ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <GridView />
            )
          }
          sx={{
            display: { xs: "inline-flex", md: "none" },
            color: "white",
            borderColor: "rgba(255,255,255,0.7)",
            minHeight: 36,
            px: 1.25,
            fontWeight: 700,
            "&:hover": {
              borderColor: "white",
              backgroundColor: "rgba(255,255,255,0.08)",
            },
          }}
        >
          Map
        </Button>
      </Box>
    </Paper>
  );
}
